import {Ray} from '@babylonjs/core/Culling/ray';
import {Matrix,Vector3} from '@babylonjs/core/Maths/math.vector';
import type {TargetCamera} from '@babylonjs/core/Cameras/targetCamera';
import {VIRGILIO_RANGE,virgilioTarget,groundHeightAt,type ArenaDefinition,type Vec3} from '@arena/shared';
import type {InputController} from './controller.ts';

interface Context {arena:ArenaDefinition;source:Vec3;ready:boolean;label?:string}

/** Two-step cast shared by touch and mouse, with no cooldown spent on selection. */
export class VirgilioTargeting {
  private armed=false;
  private selected:Vec3|null=null;
  private readonly button=document.querySelector<HTMLElement>('[data-button="Special1"]')!;
  private readonly hint=document.createElement('div');
  private readonly context:()=>Context|null;
  private readonly controls:InputController;
  private readonly canvas:HTMLCanvasElement;
  private readonly camera:TargetCamera;
  private readonly range=document.createElementNS('http://www.w3.org/2000/svg','svg');
  private readonly outline=document.createElementNS('http://www.w3.org/2000/svg','path');
  constructor(canvas:HTMLCanvasElement,camera:TargetCamera,controls:InputController,context:()=>Context|null){
    this.context=context;this.controls=controls;
    this.canvas=canvas;this.camera=camera;
    this.range.classList.add('targeting-range');this.range.setAttribute('aria-hidden','true');
    this.outline.setAttribute('fill','none');this.outline.setAttribute('stroke','#8de3ff');
    this.outline.setAttribute('stroke-width','2.5');this.outline.setAttribute('stroke-dasharray','9 7');
    this.range.append(this.outline);document.body.append(this.range);this.range.style.display='none';
    this.hint.className='targeting-hint';this.hint.hidden=true;this.hint.setAttribute('role','status');
    document.body.append(this.hint);
    controls.onPress=name=>{
      if(name!=='Special1'){this.cancel();return false;}
      const ctx=this.context();if(!ctx)return false;
      if(this.armed){this.cancel();return true;}
      if(ctx.ready){this.armed=true;this.hint.textContent=`Scegli dove evocare ${ctx.label??"Virgilio"} (${VIRGILIO_RANGE} m) · S1 per annullare`;this.update();}
      return true;
    };
    window.addEventListener('keydown',e=>{if(e.code==='Escape')this.cancel();});
    document.addEventListener('pointerdown',e=>{
      if(!this.armed || e.button!==0)return;
      const el=e.target as HTMLElement;
      if(el.closest('button,select,input,#lobby,#home'))return;
      e.preventDefault();e.stopImmediatePropagation();
      const ctx=this.context();if(!ctx){this.cancel();return;}
      const rect=canvas.getBoundingClientRect();
      const ray=Ray.CreateNew(e.clientX-rect.left,e.clientY-rect.top,rect.width,rect.height,Matrix.Identity(),camera.getViewMatrix(),camera.getProjectionMatrix());
      let nearest=Infinity,point:Vec3|null=null;
      // Pick the nearest solid's horizontal top, including roofs and terraces.
      for(const box of ctx.arena.solids){
        if(Math.abs(ray.direction.y)<1e-6)continue;
        const t=(box.max.y-ray.origin.y)/ray.direction.y;
        const x=ray.origin.x+ray.direction.x*t,z=ray.origin.z+ray.direction.z*t;
        if(t<=0 || t>=nearest || x<box.min.x || x>box.max.x || z<box.min.z || z>box.max.z)continue;
        nearest=t;point={x,y:box.max.y,z};
      }
      const valid=point && virgilioTarget(ctx.arena,ctx.source,point.x,point.z);
      if(!valid || Math.abs(valid.y-point!.y)>.1){this.hint.textContent='Punto fuori portata: scegli terreno entro 8 m · S1 annulla';return;}
      this.selected=valid;this.armed=false;controls.confirmTargetedAbility();this.update();
    },{capture:true});
  }
  take():Vec3|null {const point=this.selected;this.selected=null;return point;}
  update():void {
    if(!this.context()?.ready)this.cancel();
    this.button.classList.toggle('targeting',this.armed);
    this.button.setAttribute('aria-pressed',String(this.armed));
    this.hint.hidden=!this.armed;
    this.range.style.display=this.armed?'block':'none';
    this.updateRange();
  }
  updateRange():void {
    const ctx=this.armed?this.context():null;if(!ctx)return;
    const rect=this.canvas.getBoundingClientRect(),viewport=this.camera.viewport.toGlobal(rect.width,rect.height);
    const transform=this.camera.getViewMatrix().multiply(this.camera.getProjectionMatrix());
    this.range.setAttribute('viewBox',`0 0 ${rect.width} ${rect.height}`);
    let path='',pen=false,previousY:number|null=null;
    for(let i=0;i<=128;i++){
      const angle=i/128*Math.PI*2,x=ctx.source.x+Math.cos(angle)*VIRGILIO_RANGE,z=ctx.source.z+Math.sin(angle)*VIRGILIO_RANGE;
      const y=groundHeightAt(ctx.arena,x,z,ctx.source.y+5.01)??ctx.source.y;
      if(previousY!==null && Math.abs(y-previousY)>.5)pen=false;
      previousY=y;
      const p=Vector3.Project(new Vector3(x,y+.06,z),Matrix.Identity(),transform,viewport);
      if(p.z<=0 || p.z>=1){pen=false;continue;}
      path+=`${pen?'L':'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`;pen=true;
    }
    this.outline.setAttribute('d',path);
  }
  private cancel():void {this.armed=false;if(this.selected)this.controls.clearTargetedAbility();this.selected=null;this.button.classList.remove('targeting');this.button.setAttribute('aria-pressed','false');this.hint.hidden=true;this.range.style.display='none';}
}
